import type { Identifier } from "ra-core";

import { ROLE_LABELS } from "../sales/roles";
import type { AppRole, BusinessArm, OrgChartPerson } from "../types";

/**
 * Almikia Epic EO (TASK-098), DOMAIN RULES EO (a): the position label shown
 * for a person, derived from role + brazo because `team_roster.position` is
 * not exposed by `org_chart_people` (ASSUMPTION, gender-neutral area names).
 * Every non-`direccion` role reuses the existing `ROLE_LABELS` (`sales/roles.ts`)
 * -- it already spells "Lead Manager" / "Líder BPO" / "Asesora" /
 * "Administración" exactly as the domain rule asks for; only `direccion`
 * needs its own brazo-specific label.
 */
export function getPositionLabel(
  person: Pick<OrgChartPerson, "role" | "business_arm">,
): string {
  if (person.role !== "direccion") {
    return ROLE_LABELS[person.role];
  }
  switch (person.business_arm) {
    case "direccion_general":
      return "Dirección General";
    case "corretaje":
      return "Dirección de Corretaje";
    case "bpo":
      return "Dirección BPO";
    default:
      // 'ambos' or NULL, per DOMAIN RULES EO (a).
      return "Dirección";
  }
}

/**
 * DOMAIN RULES EO (c): the rank people are ordered by within a group/subgroup
 * -- position first, never a performance measure. `org_chart_people` (TASK-096)
 * never returns compliance/contabilidad/auditoria rows; they're kept here
 * only so the map stays exhaustive over `AppRole`.
 */
const POSITION_RANK: Record<AppRole, number> = {
  direccion: 0,
  lead_manager: 1,
  lider: 2,
  asesora: 3,
  administracion: 4,
  compliance: 5,
  contabilidad: 5,
  auditoria: 5,
};

/** DOMAIN RULES EO (c): position then name -- never any performance measure. */
export function compareOrgChartPeople(
  a: OrgChartPerson,
  b: OrgChartPerson,
): number {
  const rankDiff = POSITION_RANK[a.role] - POSITION_RANK[b.role];
  if (rankDiff !== 0) return rankDiff;
  const nameA = `${a.first_name} ${a.last_name}`;
  const nameB = `${b.first_name} ${b.last_name}`;
  return nameA.localeCompare(nameB);
}

export type OrgChartArmKey = BusinessArm | "unassigned";

/** DOMAIN RULES EO (c): section order, a `null`/undefined `business_arm` last. */
export const ORG_CHART_ARM_ORDER: OrgChartArmKey[] = [
  "direccion_general",
  "corretaje",
  "bpo",
  "ambos",
  "unassigned",
];

/**
 * Section/brazo labels (DOMAIN RULES EO (c)): fixed Spanish business
 * vocabulary, shown as-is whatever the app locale -- the same
 * locale-invariant convention `ROLE_LABELS` (`sales/roles.ts`) and
 * `BUSINESS_ARM_LABELS` (`sales/businessArms.ts`) already use, not routed
 * through `useTranslate`.
 */
export const ORG_CHART_ARM_LABELS: Record<OrgChartArmKey, string> = {
  direccion_general: "Dirección General",
  corretaje: "Corretaje",
  bpo: "BPO",
  ambos: "Corretaje y BPO",
  unassigned: "Sin unidad de negocio asignada",
};

/** The bpo group's catch-all subgroup for a person with no active project. */
export const BPO_UNASSIGNED_PROJECT_LABEL = "Sin proyecto asignado";

const armKey = (person: OrgChartPerson): OrgChartArmKey =>
  person.business_arm ?? "unassigned";

/**
 * One project sub-group inside the "bpo" arm section, or the single
 * catch-all subgroup every other arm section uses.
 */
export interface OrgChartSubgroup {
  /** A project id for a BPO subgroup, `"all"` for a non-BPO arm section, or
   * `"sin_proyecto"` for a bpo person with no active project membership. */
  key: Identifier | "all" | "sin_proyecto";
  /** The project's own name (BPO subgroups only), verbatim, never translated. */
  projectName?: string;
  people: OrgChartPerson[];
}

export interface OrgChartGroup {
  key: OrgChartArmKey;
  subgroups: OrgChartSubgroup[];
}

/**
 * DOMAIN RULES EO (c): groups people by brazo (order in {@link ORG_CHART_ARM_ORDER}),
 * with the "bpo" group broken down further into one subgroup per active
 * project -- its Líder(es) BPO first, already guaranteed by
 * {@link compareOrgChartPeople}'s position ranking (lider outranks asesora).
 * A person on several projects appears under each. Empty groups/subgroups are
 * omitted so the page never renders a heading with nothing under it.
 */
export function buildOrgChartGroups(people: OrgChartPerson[]): OrgChartGroup[] {
  const byArm = new Map<OrgChartArmKey, OrgChartPerson[]>();
  for (const person of people) {
    const key = armKey(person);
    const existing = byArm.get(key) ?? [];
    existing.push(person);
    byArm.set(key, existing);
  }

  return ORG_CHART_ARM_ORDER.filter((key) => byArm.has(key)).map((key) => {
    const armPeople = byArm.get(key)!;
    if (key !== "bpo") {
      return {
        key,
        subgroups: [
          {
            key: "all" as const,
            people: [...armPeople].sort(compareOrgChartPeople),
          },
        ],
      };
    }

    const byProject = new Map<
      Identifier,
      { projectName: string; people: OrgChartPerson[] }
    >();
    const withoutProject: OrgChartPerson[] = [];
    for (const person of armPeople) {
      if (person.projects.length === 0) {
        withoutProject.push(person);
        continue;
      }
      for (const project of person.projects) {
        const existing = byProject.get(project.project_id) ?? {
          projectName: project.name,
          people: [],
        };
        existing.people.push(person);
        byProject.set(project.project_id, existing);
      }
    }

    const projectSubgroups: OrgChartSubgroup[] = [...byProject.entries()]
      .sort(([, a], [, b]) => a.projectName.localeCompare(b.projectName))
      .map(([projectId, { projectName, people: projectPeople }]) => ({
        key: projectId,
        projectName,
        people: [...projectPeople].sort(compareOrgChartPeople),
      }));

    const subgroups: OrgChartSubgroup[] = withoutProject.length
      ? [
          ...projectSubgroups,
          {
            key: "sin_proyecto" as const,
            people: [...withoutProject].sort(compareOrgChartPeople),
          },
        ]
      : projectSubgroups;

    return { key, subgroups };
  });
}

/**
 * A person's Coordinación / Acompañamiento relationship, shown on her card
 * (DOMAIN RULES EO (b)): to her `reports_to` target when set (Acompañamiento
 * when that target is a lider, Coordinación otherwise), PLUS -- for an
 * asesora only -- one Acompañamiento entry per distinct active Líder BPO of
 * a project she's on but doesn't lead herself (she can have both a
 * reports_to Coordinación/Acompañamiento AND one or more project
 * Acompañamiento links at once). Empty when none applies (e.g. Dirección
 * General, who reports to nobody in the chart).
 */
export interface OrgChartRelationship {
  label: "Coordinación" | "Acompañamiento";
  counterpart: OrgChartPerson;
}

export function getCardRelationships(
  person: OrgChartPerson,
  peopleById: Map<Identifier, OrgChartPerson>,
): OrgChartRelationship[] {
  const relationships: OrgChartRelationship[] = [];
  const seenLiderIds = new Set<Identifier>();

  if (person.reports_to != null) {
    const target = peopleById.get(person.reports_to);
    if (target) {
      relationships.push({
        label: target.role === "lider" ? "Acompañamiento" : "Coordinación",
        counterpart: target,
      });
      seenLiderIds.add(target.id);
    }
  }

  if (person.role === "asesora") {
    for (const project of person.projects) {
      if (project.is_lider_for_project) continue;
      const lider = [...peopleById.values()].find(
        (candidate) =>
          candidate.id !== person.id &&
          candidate.projects.some(
            (candidateProject) =>
              candidateProject.project_id === project.project_id &&
              candidateProject.is_lider_for_project,
          ),
      );
      if (lider && !seenLiderIds.has(lider.id)) {
        seenLiderIds.add(lider.id);
        relationships.push({ label: "Acompañamiento", counterpart: lider });
      }
    }
  }

  return relationships;
}

/** Builds an id-keyed lookup once per render, for {@link getCardRelationships}. */
export function indexOrgChartPeopleById(
  people: OrgChartPerson[],
): Map<Identifier, OrgChartPerson> {
  return new Map(people.map((person) => [person.id, person]));
}
